/**
 * Filtering and the derived flags the table and the badges read.
 *
 * Kept out of the component files so those modules export only components, which is
 * the react-refresh rule the project lints for (same reason as
 * pages/Hipaa/utils/checklistFilters.ts).
 */
import { STALLED_AFTER_MS } from '../services/accountDeletionsService';
import type {
  AccountDeletionRecord,
  DeletionSource,
  DeletionStatus,
} from '../types/accountDeletionTypes';

/**
 * The alert threshold from docs/ai-plans-and-docs/revenue-cat-phi-handling.md §11:
 * a job the reconciler has failed this many times needs a person.
 */
export const ATTENTION_ATTEMPTS = 3;

export interface DeletionFilters {
  /** 'attention' is the cross-cutting "look at these" view, not a status. */
  status: DeletionStatus | 'all' | 'attention';
  source: DeletionSource | 'all';
  search: string;
}

export const DEFAULT_FILTERS: DeletionFilters = {
  status: 'all',
  source: 'all',
  search: '',
};

/** Filters live in the query string so a filtered view can be pasted to someone else. */
export const filtersFromParams = (params: URLSearchParams): DeletionFilters => ({
  status: (params.get('status') as DeletionFilters['status']) ?? DEFAULT_FILTERS.status,
  source: (params.get('source') as DeletionFilters['source']) ?? DEFAULT_FILTERS.source,
  search: params.get('q') ?? DEFAULT_FILTERS.search,
});

/** Only non-default values are written, which keeps the common URL clean. */
export const filtersToParams = (filters: DeletionFilters): Record<string, string> => {
  const params: Record<string, string> = {};
  if (filters.status !== DEFAULT_FILTERS.status) params.status = filters.status;
  if (filters.source !== DEFAULT_FILTERS.source) params.source = filters.source;
  if (filters.search) params.q = filters.search;
  return params;
};

const millisOf = (record: AccountDeletionRecord): number =>
  record.updatedAt && typeof record.updatedAt.toMillis === 'function'
    ? record.updatedAt.toMillis()
    : 0;

/** Failed often enough that the reconciler is not going to fix it on its own. */
export const needsAttention = (record: AccountDeletionRecord): boolean =>
  record.status === 'failed' && record.attemptCount >= ATTENTION_ATTEMPTS;

/** Unfinished and untouched long enough that a reconciler run has already missed it. */
export const isStalled = (record: AccountDeletionRecord): boolean => {
  if (record.status === 'completed') return false;
  const updated = millisOf(record);
  return updated > 0 && Date.now() - updated > STALLED_AFTER_MS;
};

/**
 * A record that claims success while still holding a failure.
 *
 * runAccountDeletion() completes a job only when RevenueCat, PostHog and every location
 * succeeded, so this combination should not exist. When it does, the audit trail is
 * wrong -- the same class of fault as the `audit=completed` case in
 * tools/src/users/audit-revenuecat-orphans.ts, and worth surfacing rather than
 * trusting.
 */
export const isInconsistent = (record: AccountDeletionRecord): boolean => {
  if (record.status !== 'completed') return false;
  if (record.revenueCat === null || record.revenueCat.outcome === 'failed') return true;
  // null is allowed: records completed before the PostHog step existed have none.
  if (record.posthog?.outcome === 'failed') return true;
  return Object.values(record.firestore).some((entry) => entry.status === 'failed');
};

/**
 * Locations and documents are different numbers and must never be shown as one.
 *
 * A sweep that ran over all seven registered locations and found nothing to delete is
 * the normal case, and reporting it as "7 deleted" reads as seven documents removed.
 * `documentsDeleted` is the real figure; the location counts say how much of the sweep
 * ran and whether any part of it failed.
 */
export interface SweepSummary {
  /** Locations that reported success, whether or not they held anything. */
  locationsDeleted: number;
  /** Locations that reported a failure. */
  locationsFailed: number;
  /** Documents actually removed, summed across every location. */
  documentsDeleted: number;
  /** True when step 3 never ran, which is different from "nothing to delete". */
  notReached: boolean;
}

export const sweepSummary = (record: AccountDeletionRecord): SweepSummary => {
  const entries = Object.values(record.firestore);
  return {
    locationsDeleted: entries.filter((entry) => entry.status === 'deleted').length,
    locationsFailed: entries.filter((entry) => entry.status === 'failed').length,
    documentsDeleted: entries.reduce((total, entry) => total + (entry.documentsDeleted ?? 0), 0),
    notReached: entries.length === 0,
  };
};

export const matchesFilters = (
  record: AccountDeletionRecord,
  filters: DeletionFilters,
): boolean => {
  if (filters.status === 'attention') {
    if (!needsAttention(record) && !isStalled(record) && !isInconsistent(record)) return false;
  } else if (filters.status !== 'all' && record.status !== filters.status) {
    return false;
  }

  if (filters.source !== 'all' && record.source !== filters.source) return false;

  // The uid is the only thing there is to search: the record holds no other identifier.
  if (filters.search && !record.uid.toLowerCase().includes(filters.search.toLowerCase())) {
    return false;
  }

  return true;
};
